import {
  ApproverType,
  ITimeAmount,
  RequesterType,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";

export enum PresenceType {
  Attendance = "ATTENDANCE",
  Absence = "ABSENCE",
}

export enum BillingCycleConfigurationType {
  Yearly = "YEARLY",
  Monthly = "MONTHLY",
}

export enum EPatternUnit {
  Hour = "HOUR",
  Day = "DAY",
}

export enum SubmitLimitationType {
  // only one for now
  RELATIVE = "RELATIVE",
}

export enum SubmitLimitationPeriodType {
  WEEK = "WEEK",
  MONTH = "MONTH",
  QUARTER = "QUARTER",
  HALF_YEAR = "HALF_YEAR",
  YEAR = "YEAR",
}

export enum EEntitledEmployeesType {
  All = "ALL",
  Explicit = "EXPLICIT",
}

export enum FieldType {
  TEXT = "TEXT",
  DATE = "DATE",
  CHECKBOX = "CHECKBOX",
  CHOICE_SELECTION = "CHOICE_SELECTION",
}

export enum ApproversType {
  AUTO = "AUTO",
  MANUAL = "MANUAL",
}

export enum BillingCycle {
  NONE = "NONE",
  MONTHLY = "MONTHLY",
}

export enum Hierarchy {
  MAIN = "MAIN",
  CHILD = "CHILD",
}

export enum AbsenceTypeAction {
  CREATION = "CREATION",
  UPDATE = "UPDATE",
}

export enum AbsenceTypeOperation {
  ABSENCE_REQUEST_CREATED = "ABSENCE_REQUEST_CREATED",
  ABSENCE_REQUEST_CANCELLED = "ABSENCE_REQUEST_CANCELLED",
  ABSENCE_REQUEST_ACCEPTED = "ABSENCE_REQUEST_ACCEPTED",
  ABSENCE_REQUEST_EDITED = "ABSENCE_REQUEST_EDITED",
  ABSENCE_REQUEST_REJECTED = "ABSENCE_REQUEST_REJECTED",
  BALANCE_CREATED = "BALANCE_CREATED",
  BALANCE_MODIFIED = "BALANCE_MODIFIED",
  BALANCE_MODIFIED_PATTERN_EXECUTION = "BALANCE_MODIFIED_PATTERN_EXECUTION",
  PATTERN_ASSIGNMENT_CHANGED = "PATTERN_ASSIGNMENT_CHANGED",
  PARENT_PATTERN_ASSIGNMENT_CHANGED = "PARENT_PATTERN_ASSIGNMENT_CHANGED",
  CHILD_ABSENCE_REQUEST_CREATED = "CHILD_ABSENCE_REQUEST_CREATED",
  PARENT_ABSENCE_REQUEST_CREATED = "PARENT_ABSENCE_REQUEST_CREATED",
  SIBLING_ABSENCE_REQUEST_CREATED = "SIBLING_ABSENCE_REQUEST_CREATED",
  CHILD_ABSENCE_REQUEST_CANCELLED = "CHILD_ABSENCE_REQUEST_CANCELLED",
  PARENT_ABSENCE_REQUEST_CANCELLED = "PARENT_ABSENCE_REQUEST_CANCELLED",
  SIBLING_ABSENCE_REQUEST_CANCELLED = "SIBLING_ABSENCE_REQUEST_CANCELLED",
  CHILD_ABSENCE_REQUEST_REJECTED = "CHILD_ABSENCE_REQUEST_REJECTED",
  PARENT_ABSENCE_REQUEST_REJECTED = "PARENT_ABSENCE_REQUEST_REJECTED",
  SIBLING_ABSENCE_REQUEST_REJECTED = "SIBLING_ABSENCE_REQUEST_REJECTED",
  CHILD_ABSENCE_REQUEST_ACCEPTED = "CHILD_ABSENCE_REQUEST_ACCEPTED",
  CHILD_ABSENCE_REQUEST_EDITED = "CHILD_ABSENCE_REQUEST_EDITED",
  PARENT_ABSENCE_REQUEST_EDITED = "PARENT_ABSENCE_REQUEST_EDITED",
  SIBLING_ABSENCE_REQUEST_EDITED = "SIBLING_ABSENCE_REQUEST_EDITED",
  PARENT_BALANCE_MODIFIED = "PARENT_BALANCE_MODIFIED",
  PARENT_BALANCE_MODIFIED_PATTERN_EXECUTION = "PARENT_BALANCE_MODIFIED_PATTERN_EXECUTION",
  BALANCE_EXPIRED = "BALANCE_EXPIRED",
  BALANCE_CARRYOVER = "BALANCE_CARRYOVER",
  PARENT_AND_OWN_PATTERN_ASSIGNMENT_CHANGED = "PARENT_AND_OWN_PATTERN_ASSIGNMENT_CHANGED",
  DAILY_WORKING_HOURS_CHANGED = "DAILY_WORKING_HOURS_CHANGED",
  ABSENCE_REQUESTS_IMPORTED = "ABSENCE_REQUESTS_IMPORTED",
  SUBMIT_LIMITATIONS_CHANGED = "SUBMIT_LIMITATIONS_CHANGED",
  PARENT_SUBMIT_LIMITATIONS_CHANGED = "PARENT_SUBMIT_LIMITATIONS_CHANGED",
}

export enum ValueSign {
  POSITIVE = "POSITIVE",
  NEGATIVE = "NEGATIVE",
}

export enum NotificationType {
  EMAIL = "EMAIL",
}

export interface BillingCyclePattern {
  id?: string;
  value: number;
}

export interface BillingCyclePatternForm {
  id?: string;
  value: number;
}

export interface BillingCycleConfiguration {
  type: BillingCycleConfigurationType | null;
  parentAbsenceTypeId: string | null;
  patterns: BillingCyclePattern[];
  stackUnusedTimeForNextBillingCycle: boolean;
}

export interface BillingCycleConfigurationForm
  extends Omit<BillingCycleConfiguration, "patterns"> {
  parentSelectActive: boolean;
  patterns: BillingCyclePatternForm[];
}

export interface SubmitLimitation {
  type: SubmitLimitationType;
  count: number;
  periodType: SubmitLimitationPeriodType;
}

interface SubmitLimitationForm {
  prior: SubmitLimitation;
  advance: SubmitLimitation;
}

interface IAbsenceTypeConfigurationOptionsBase {
  absencesVisibleForOthers: boolean;
  billInHours: boolean;
  billInCalendarDays: boolean;
  multipleRequestsPerDayAllowed: boolean;
  modificationAllowed: boolean;
  commentsAllowed: boolean;
  justificationRequired: boolean;
  attachmentRequired: boolean;
  replacerRequired: boolean;
}

export interface IAbsenceTypeConfigurationOptions
  extends IAbsenceTypeConfigurationOptionsBase {
  submitLimitations: {
    prior: SubmitLimitation;
    advance: SubmitLimitation;
  };
}

export interface IAbsenceTypeConfigurationOptionsForm
  extends IAbsenceTypeConfigurationOptionsBase {
  submitLimitations: SubmitLimitationForm;
}

export interface INotificationSetting {
  [key: string]: NotificationType[];
}

export interface INotificationsConfiguration {
  approverNotifications: {
    absenceRequestCreation: NotificationType[];
    absenceRequestEdition: NotificationType[];
  };
  immediateSuperiorAndApproverNotifications: {
    differentApproverAbsenceRequestAcceptation: NotificationType[];
    differentApproverAbsenceRequestAutoAcceptation: NotificationType[];
    longAbsenceRequestEndReminder: NotificationType[];
  };
  employeeNotifications: {
    absenceRequestCreation: NotificationType[];
    absenceRequestAcceptation: NotificationType[];
    absenceRequestRejection: NotificationType[];
    absenceRequestCancellation: NotificationType[];
    absenceRequestSentForCorrection: NotificationType[];
    absenceRequestEdition: NotificationType[];
  };
  ahrNotifications: {
    longAbsenceRequestEndReminder: NotificationType[];
  };
}

export interface IAbsenceType {
  id: string;
  ownerId: string;
  isMain: boolean;
  name: string;
  description: string;
  active: boolean;
  fullyEditable: boolean;
  absenceGroupId: string;
  presenceType: PresenceType;
  billingCycleConfiguration: BillingCycleConfiguration | null;
  entitledEmployees: {
    type: EEntitledEmployeesType;
    employmentTypes: string[]; // ["uop","b2b"]
    employeeGroups: string[];
    organizationUnits: string[];
    costPlaces: string[]; // ["warsaw", "wroclaw"]
  };
  configurationOptions: IAbsenceTypeConfigurationOptions;
  notificationsConfiguration: INotificationsConfiguration;
  approvers: ApproverType[];
  customFieldsConfiguration: ICustomFieldsConfiguration | null;
  isStackUnusedTimeForNextBillingCycleEditable: boolean;
}

export interface IAbsenceTypeListItem extends IAbsenceType {
  color: string;
  parentName: string;
}

export interface IAbsenceTypeForm
  extends Omit<
    IAbsenceType,
    "billingCycleConfiguration" | "configurationOptions"
  > {
  hierarchy: Hierarchy;
  approversType: ApproversType;
  billingCycle: BillingCycle;
  billingCycleConfiguration: BillingCycleConfigurationForm | null;
  configurationOptions: IAbsenceTypeConfigurationOptionsForm;
}

export interface ICustomFieldsConfiguration {
  customFields: ICustomFieldDTO[];
}

interface ICustomFieldBase {
  fieldType: FieldType;
  name: string;
  description?: string;
  label?: string;
  required: boolean;
  multipleChoice?: boolean;
}

export interface ICustomField extends ICustomFieldBase {
  id: string;
  isNew?: boolean;
  choices?: { id: string; value: string }[];
}

export interface ICustomFieldDTO extends ICustomFieldBase {
  customFieldId?: string;
  choices?: string[];
}

export interface IRequester {
  type: RequesterType;
  id: string;
  firstName: string;
  lastName: string;
  // TODO: consult with the backend wtf is in metadata
  metadata: object;
}

interface IAbsenceTypeChange<T> {
  before: T;
  after: T;
}

export enum AbsenceTypeHistoryEntryDetailsTypes {
  NAME = "name",
  DESCRIPTION = "description",
  ACTIVE = "active",
  BILLING_CYCLE_DURATION = "billingCycleDuration",
  STACK_UNUSED_TIME_FOR_NEXT_BILLING_CYCLE = "stackUnusedTimeForNextBillingCycle",
  ENTITLED_EMPLOYEES = "entitledEmployees",
  CONFIGURATION_OPTIONS = "configurationOptions",
  SUBMIT_LIMITATIONS = "submitLimitations",
  NOTIFICATIONS_CONFIGURATION = "notificationsConfiguration",
  CUSTOM_FIELDS_CONFIGURATION = "customFieldsConfiguration",
  PRESENCE_TYPE = "presenceType",
  ABSENCE_GROUP = "absenceGroup",
  PARENT = "parent",
  APPROVER_TYPES = "approverTypes",
  AVAILABLE_ABSENCE_PATTERNS = "availableAbsencePatterns",
}

export interface IEntitledEmployeesType {
  type: EEntitledEmployeesType;
  employmentTypes: string[];
  employeeGroups: { id: string; name: string }[];
  organizationUnits: { id: string; name: string }[];
  costPlaces: string[];
}

export type ConfigurationOptionsChangeType = {
  [K in keyof IAbsenceTypeConfigurationOptions]: K extends "submitLimitations"
    ? {
        prior: IAbsenceTypeChange<SubmitLimitation> | null;
        advance: IAbsenceTypeChange<SubmitLimitation> | null;
      }
    : IAbsenceTypeChange<IAbsenceTypeConfigurationOptions[K]> | null;
};

type NotificationsConfigurationChangeType = {
  [K in keyof INotificationsConfiguration]: {
    [SubK in keyof INotificationsConfiguration[K]]: IAbsenceTypeChange<
      NotificationType[]
    > | null;
  };
};

export interface IAbsenceGroup {
  id: string;
  name: string;
}

export interface IParentAbsenceType {
  id: string;
  name: string;
}

export interface IAbsenceTypeHistoryItemDetails {
  [AbsenceTypeHistoryEntryDetailsTypes.NAME]: IAbsenceTypeChange<string> | null;
  [AbsenceTypeHistoryEntryDetailsTypes.DESCRIPTION]: IAbsenceTypeChange<string> | null;
  [AbsenceTypeHistoryEntryDetailsTypes.ACTIVE]: IAbsenceTypeChange<boolean> | null;
  [AbsenceTypeHistoryEntryDetailsTypes.BILLING_CYCLE_DURATION]: IAbsenceTypeChange<string> | null;
  [AbsenceTypeHistoryEntryDetailsTypes.STACK_UNUSED_TIME_FOR_NEXT_BILLING_CYCLE]: IAbsenceTypeChange<boolean> | null;
  [AbsenceTypeHistoryEntryDetailsTypes.ENTITLED_EMPLOYEES]: IAbsenceTypeChange<IEntitledEmployeesType> | null;
  [AbsenceTypeHistoryEntryDetailsTypes.CONFIGURATION_OPTIONS]: ConfigurationOptionsChangeType | null;
  [AbsenceTypeHistoryEntryDetailsTypes.NOTIFICATIONS_CONFIGURATION]: NotificationsConfigurationChangeType | null;
  [AbsenceTypeHistoryEntryDetailsTypes.CUSTOM_FIELDS_CONFIGURATION]: IAbsenceTypeChange<
    ICustomFieldDTO[]
  > | null;
  [AbsenceTypeHistoryEntryDetailsTypes.PRESENCE_TYPE]: IAbsenceTypeChange<PresenceType> | null;
  [AbsenceTypeHistoryEntryDetailsTypes.ABSENCE_GROUP]: IAbsenceTypeChange<IAbsenceGroup> | null;
  [AbsenceTypeHistoryEntryDetailsTypes.PARENT]: IAbsenceTypeChange<IParentAbsenceType> | null;
  [AbsenceTypeHistoryEntryDetailsTypes.APPROVER_TYPES]: IAbsenceTypeChange<
    ApproverType[]
  > | null;
  [AbsenceTypeHistoryEntryDetailsTypes.AVAILABLE_ABSENCE_PATTERNS]: IAbsenceTypeChange<
    number[]
  > | null;
}

export const HistoryEntryDetailsLabels = {
  [AbsenceTypeHistoryEntryDetailsTypes.NAME]: "Nazwa wniosku",
  [AbsenceTypeHistoryEntryDetailsTypes.DESCRIPTION]: "Opis",
  [AbsenceTypeHistoryEntryDetailsTypes.ACTIVE]: "Widoczność szablonu",
  [AbsenceTypeHistoryEntryDetailsTypes.BILLING_CYCLE_DURATION]:
    "Rozliczanie wymiaru",
  [AbsenceTypeHistoryEntryDetailsTypes.STACK_UNUSED_TIME_FOR_NEXT_BILLING_CYCLE]:
    "Przenoszenie zaległych dni na następny okres",
  [AbsenceTypeHistoryEntryDetailsTypes.ENTITLED_EMPLOYEES]: "Odbiorcy szablonu",
  [AbsenceTypeHistoryEntryDetailsTypes.CONFIGURATION_OPTIONS]: "Cechy szablonu",
  [AbsenceTypeHistoryEntryDetailsTypes.NOTIFICATIONS_CONFIGURATION]:
    "Powiadomienia mailowe",
  [AbsenceTypeHistoryEntryDetailsTypes.CUSTOM_FIELDS_CONFIGURATION]:
    "Dodatkowe pola",
  [AbsenceTypeHistoryEntryDetailsTypes.PRESENCE_TYPE]: "Rodzaj szablonu",
  [AbsenceTypeHistoryEntryDetailsTypes.ABSENCE_GROUP]: "Grupa szablonu",
  [AbsenceTypeHistoryEntryDetailsTypes.PARENT]: "Szablon nadrzędny",
  [AbsenceTypeHistoryEntryDetailsTypes.APPROVER_TYPES]:
    "Sposób akceptacji wniosku",
  [AbsenceTypeHistoryEntryDetailsTypes.AVAILABLE_ABSENCE_PATTERNS]:
    "Dostępne wzorce",
};

export interface IAbsenceTypeHistoryItem {
  action: AbsenceTypeAction;
  actionTime: string;
  requester: IRequester;
  details: IAbsenceTypeHistoryItemDetails;
  order: number;
}

export interface IAbsenceTypeHistory {
  entries: IAbsenceTypeHistoryItem[];
}

export type AbsenceTypeHistoryTableValue =
  | IEntitledEmployeesType
  | PresenceType
  | IParentAbsenceType
  | SubmitLimitation
  | boolean
  | string
  | string[]
  | number[]
  | null;

export interface IAbsenceTypeHistoryTableEntry {
  id: string;
  parentId?: string;
  configKey?: string;
  groupKey?: string;
  notificationKey?: string;
  submitLimitationType?: string;
  before: AbsenceTypeHistoryTableValue;
  after: AbsenceTypeHistoryTableValue;
}

export interface IAbsenceTypeBalanceHistoryItem {
  artificialId: number;
  operationTime: string;
  billingCycle: {
    billingCycleType: BillingCycleConfigurationType;
    year?: number;
    yearMonth: string;
  };
  balanceChange: {
    timeAmount: ITimeAmount;
    valueSign: ValueSign;
  };
  balanceAfterOperation: {
    timeAmount: ITimeAmount;
    valueSign: ValueSign;
  };
  operationInfo: {
    absenceRequestId?: string;
    operation: AbsenceTypeOperation;
    note?: string;
  };
  requester: IRequester;
}

export interface IAbsenceTypeBalanceHistory {
  content: IAbsenceTypeBalanceHistoryItem[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
}

export interface IEmployeeGroup {
  id: string;
  name: string;
}

export interface IOrganizationUnit {
  id: string;
  name: string;
}
